import React from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormCheck, FormInput } from "go-form";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { KSeFEnvironmentType } from "../../../../../../../../../../../../services/Api/Organization/types";
import { IntegrationWithKSeFModalProps } from "../../utils/types";
import { KSeFCertificateInputs } from "./KSeFCertificateInputs";
import { KSeFCertificateIssuedInfo } from "./KSeFCertificateIssuedInfo";

export const IntegrationWithKSeFModal = ({
	show,
	onHide,
	form,
	onSubmit,
	loading,
	KSeFInstalled,
	KSeFTermsAccepted,
}: IntegrationWithKSeFModalProps) => {
	const { t } = useTranslation();
	const {
		register,
		control,
		formState: { errors },
		watch,
		setValue,
		clearErrors,
	} = form;
	const confirmation = useConfirmation();
	const {
		should_delete_ksef_auth_cert: watchedShouldDeleteKSeFAuthCert,
		should_delete_ksef_offline_cert: watchedShouldDeleteKSeFOfflineCert,
		ksef_auth_cert: watchedKSeFAuthCert,
		ksef_offline_cert: watchedKSeFOfflineCert,
		ksef_token: watchedKSeFToken,
		ksef_environment_type: watchedKSeFEnvironmentType,
	} = watch();
	const KSeFEnvironmentTypes: { value: KSeFEnvironmentType; label: string }[] = [
		{ value: "TEST", label: t("enums.invoices.KSeF_environment.TEST") },
		{ value: "PROD", label: t("enums.invoices.KSeF_environment.PROD") },
	];

	const environmentTypeChangeHandler = async (newValue: string) => {
		if (watchedKSeFEnvironmentType === newValue) return;

		try {
			if (
				(watchedKSeFAuthCert && !watchedShouldDeleteKSeFAuthCert) ||
				(watchedKSeFOfflineCert && !watchedShouldDeleteKSeFOfflineCert) ||
				watchedKSeFToken
			) {
				await confirmation({
					title: t("lib:confirmation.title"),
					message: t("modules.invoice.field.clear_ksef_inputs_warning.title"),
				});
			}

			setValue("ksef_crt_offline_cert_file", undefined);
			setValue("ksef_key_offline_cert_file", undefined);
			setValue("ksef_auth_cert_password", "");
			setValue("ksef_crt_auth_cert_file", undefined);
			setValue("ksef_key_auth_cert_file", undefined);
			setValue("ksef_offline_cert_password", "");
			setValue("should_delete_ksef_auth_cert", true);
			setValue("should_delete_ksef_offline_cert", true);
			setValue("ksef_token", "");
			setValue("send_to_ksef_automatically", false);
			clearErrors([
				"ksef_crt_offline_cert_file",
				"ksef_key_offline_cert_file",
				"ksef_crt_auth_cert_file",
				"ksef_key_auth_cert_file",
			]);
		} catch (err) {
			const oldInputValue = newValue === "TEST" ? "PROD" : "TEST";
			setValue("ksef_environment_type", oldInputValue);
		}
	};

	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{t("modules.invoice.field.ksef_integration.title")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<FormSelectGroup
					errors={errors}
					name="ksef_environment_type"
					label={t("modules.invoice.field.ksef_environment_type.title")}
					control={control}
					options={KSeFEnvironmentTypes}
					onChange={environmentTypeChangeHandler}
				/>
				<h6 className="text-secondary mb-3">{t("modules.invoice_details.field.ksef_auth_cert.title")}</h6>
				{!watchedShouldDeleteKSeFAuthCert && watchedKSeFAuthCert ? (
					<KSeFCertificateIssuedInfo
						createdAt={watchedKSeFAuthCert.created_at}
						activeUntil={watchedKSeFAuthCert.active_until}
						setValue={setValue}
						removeKey="should_delete_ksef_auth_cert"
					/>
				) : (
					<KSeFCertificateInputs
						form={form}
						fileInputNames={["ksef_crt_auth_cert_file", "ksef_key_auth_cert_file"]}
						filePasswordInputName="ksef_auth_cert_password"
						filePasswordLabel={t("modules.invoice_details.field.ksef_auth_cert.password")}
					/>
				)}
				<hr />
				<h6 className="text-secondary mb-3 mt-4">
					{t("modules.invoice_details.field.ksef_offline_cert.title")}
				</h6>
				{!watchedShouldDeleteKSeFOfflineCert && watchedKSeFOfflineCert ? (
					<KSeFCertificateIssuedInfo
						createdAt={watchedKSeFOfflineCert.created_at}
						activeUntil={watchedKSeFOfflineCert.active_until}
						setValue={setValue}
						removeKey="should_delete_ksef_offline_cert"
					/>
				) : (
					<KSeFCertificateInputs
						form={form}
						fileInputNames={["ksef_crt_offline_cert_file", "ksef_key_offline_cert_file"]}
						filePasswordInputName="ksef_offline_cert_password"
						filePasswordLabel={t("modules.invoice_details.field.ksef_offline_cert.password")}
					/>
				)}
				<hr />
				<FormInput
					register={register}
					errors={errors}
					name="ksef_token"
					label={t("modules.invoice_details.field.ksef_token.title")}
					autoComplete="off"
				/>
				<FormCheck
					name="send_to_ksef_automatically"
					register={register}
					errors={errors}
					type="switch"
					label={t("modules.invoice_details.field.send_to_ksef_automatically.title")}
					disabled={!KSeFInstalled}
					className="ksef-integration-modal-checkbox"
				/>
				{!KSeFTermsAccepted && (
					<FormCheck
						name="accept_store_ksef_sensitive_data_terms"
						errors={errors}
						register={register}
						registerOptions={{ required: "terms_acceptance_missing" }}
						label={t("modules.invoice_details.field.accept_store_ksef_sensitive_data_terms.title")}
						className="mt-2 ksef-integration-modal-checkbox"
					/>
				)}
			</Modal.Body>
			<Modal.Footer data-testid="modal-footer">
				<ButtonLoading loading={loading} onClick={onSubmit}>
					{t(`lib:common.action.${KSeFInstalled ? "save" : "install"}`)}
				</ButtonLoading>
				<Button variant="light" onClick={onHide}>
					{t("lib:common.action.cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};
